const COOKIE = 'fluenceur_analytics_choice';
const DENIED_ACCOUNTS = 'fluenceur_analytics_denied_accounts';
let currentAccountId: string | null = null;
const memoryDeniedAccounts = new Set<string>();

function deniedAccounts(): string[] {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(DENIED_ACCOUNTS) ?? '[]');
    return [
      ...new Set([
        ...memoryDeniedAccounts,
        ...(Array.isArray(value)
          ? value.filter((item): item is string => typeof item === 'string')
          : []),
      ]),
    ];
  } catch {
    return [...memoryDeniedAccounts];
  }
}

/** A denial-only local safeguard, scoped to the account; it can never grant permission. */
export function isAnalyticsAccountSuppressed(userId: string): boolean {
  return deniedAccounts().includes(userId);
}

export function setAnalyticsAccountSuppressed(userId: string, suppressed: boolean) {
  const accounts = deniedAccounts().filter((id) => id !== userId);
  if (suppressed) {
    accounts.push(userId);
    memoryDeniedAccounts.add(userId);
  } else memoryDeniedAccounts.delete(userId);
  try {
    localStorage.setItem(DENIED_ACCOUNTS, JSON.stringify(accounts));
  } catch {
    /* In-memory withdrawal remains effective. */
  }
}

export function setAnalyticsAccount(userId: string | null) {
  currentAccountId = userId;
}

export function analyticsSuppressionHeaders(): Record<string, string> {
  return currentAccountId && isAnalyticsAccountSuppressed(currentAccountId)
    ? { 'x-product-analytics-consent': 'denied' }
    : {};
}

export function readAnalyticsChoice(): 'granted' | 'denied' | null {
  const value = document.cookie
    .split('; ')
    .find((item) => item.startsWith(`${COOKIE}=`))
    ?.split('=')[1];
  return value === 'granted' || value === 'denied' ? value : null;
}

export function writeAnalyticsChoice(enabled: boolean) {
  document.cookie = `${COOKIE}=${enabled ? 'granted' : 'denied'}; Path=/; SameSite=Lax; Max-Age=15552000${window.location.protocol === 'https:' ? '; Secure' : ''}`;
}

export function clearAnalyticsChoice() {
  document.cookie = `${COOKIE}=; Path=/; SameSite=Lax; Max-Age=0`;
  document.cookie = 'fluenceur_analytics_visitor=; Path=/; SameSite=Lax; Max-Age=0';
}

export function analyticsVisitorId() {
  const existing = document.cookie
    .split('; ')
    .find((item) => item.startsWith('fluenceur_analytics_visitor='))
    ?.split('=')[1];
  if (existing && /^[0-9a-f-]{36}$/i.test(existing)) return existing;
  const value = crypto.randomUUID();
  document.cookie = `fluenceur_analytics_visitor=${value}; Path=/; SameSite=Lax; Max-Age=15552000${window.location.protocol === 'https:' ? '; Secure' : ''}`;
  return value;
}

/** Consent is forwarded only while creating an account, never while restoring a session. */
export function signupAnalyticsHeaders(url?: string): Record<string, string> {
  const choice = readAnalyticsChoice();
  return choice &&
    (url === '/auth/brands/register' ||
      /^\/brand-signup\/invitations\/[^/]+\/accept$/.test(url ?? '') ||
      /^\/team\/invitations\/[^/]+\/accept$/.test(url ?? ''))
    ? { 'x-product-analytics-consent': choice }
    : {};
}
